import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink } from "../components/Buttons";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../data/site";

const SERVICES = [
  ["Marketing Strategy", "Plans built around revenue, not vanity metrics."],
  ["Social Media Content", "Feeds that stay alive between campaigns."],
  ["Campaign Development", "Concepts, creative and rollout — one team."],
  ["Monthly Content Creation", "A standing creative department, on retainer."],
  ["Advertising Creative", "Video, photo and design versioned for every placement."],
  ["Graphic Design", "Print, digital, decks and everything between."],
  ["Photography & Video", "The raw material every channel runs on."],
  ["Website Content", "Pages that say something, built to rank and convert."],
];

const PARTNERSHIP = [
  ["Create", "Standing shoot days and a content calendar we own. Your channels never go quiet."],
  ["Market", "Campaigns, ad creative and social that put the work in front of the right people."],
  ["Grow", "Monthly reviews of what performed — then more of what worked. Simple as that."],
];

export default function Marketing() {
  useDocTitle("Marketing");
  return (
    <div data-testid="marketing-page">
      <PageHero
        eyebrow="Marketing & Content"
        lines={[<>Great content</>, <span key="g" className="text-gold-dark">deserves a plan.</span>]}
        sub="Marketing strategy, social content, campaigns and monthly creation — Studio Solutions as your ongoing creative partner, not a one-off vendor."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="marketing-hero-cta">Explore a Partnership</GoldLink>
        </div>
      </PageHero>

      <Marquee items={["Strategy", "Social Content", "Campaigns", "Ad Creative", "Graphic Design", "Photography", "Video", "Web"]} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="What Marketing Covers"
          title={<>Build the brand. Tell the story. <span className="text-gold-dark">Get it seen.</span></>}
          sub="Everything below can happen once — or every month, as a partnership."
        />
        <div className="mt-14 grid gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map(([title, copy], i) => (
            <Reveal key={title} delay={i * 0.05} className="bg-sand">
              <div
                data-testid={`marketing-service-${title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
                className="group h-full p-7 transition-colors duration-500 hover:bg-gold-light"
              >
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold-dark">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-ink">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-smoke">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STRATEGY + CREATION + EXECUTION */}
      <section className="bg-night text-sand">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-8 sm:py-24 lg:grid-cols-2 lg:px-16">
          <SectionHeading
            dark
            eyebrow="The Advantage"
            title={<>Strategy + Creation + <span className="text-gold">Execution.</span></>}
          />
          <Reveal delay={0.1}>
            <p className="font-serif text-xl italic leading-relaxed text-sand/70 sm:text-2xl">
              A traditional agency develops the idea and outsources the production. A production
              company waits for someone else's idea.
            </p>
            <p className="mt-5 font-display text-3xl font-extrabold tracking-tight text-gold">We do both.</p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-sand/60">
              One team helps you decide what's worth saying — then actually films it, designs it,
              and ships it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-ink/10 bg-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-2 lg:px-16">
          <div>
            <SectionHeading
              eyebrow="Web & Digital"
              title={<>Your website should work <span className="text-gold-dark">as hard as you do.</span></>}
              sub="Website strategy and design, landing pages, digital campaigns — and the photo and video content that fills them."
            />
            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-3.5">
                {[
                  "Website strategy, design & builds",
                  "Landing pages for campaigns",
                  "Digital ad creative & versioning",
                  "Content that ranks and converts",
                ].map((t) => (
                  <li key={t} className="flex gap-3 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/60">
                    <span className="text-gold">◆</span>
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="clip-frame overflow-hidden shadow-2xl">
              <MediaImg src={IMAGES.web} alt="Website design in progress" className="h-[380px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-night text-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <SectionHeading
            dark
            eyebrow="Monthly Partnerships"
            title={<>An ongoing creative partner, <span className="text-gold">not another vendor.</span></>}
            sub="One relationship covers strategy, creation and publishing rhythm. No pricing tables here — every partnership is scoped to your goals."
          />
          <div className="mt-14 grid gap-px bg-sand/10 lg:grid-cols-3">
            {PARTNERSHIP.map(([title, copy], i) => (
              <Reveal key={title} delay={i * 0.08} className="bg-night">
                <div className="h-full p-8 transition-colors duration-500 hover:bg-sand/5 sm:p-10">
                  <p className="font-mono text-sm tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-3.5 text-sm leading-relaxed text-sand/60">{copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10">
              <GoldLink to="/start-a-project" testId="marketing-partnership-cta">Explore a Partnership</GoldLink>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Marketing that compounds."
        sub="Tell us where the business needs to be in a year. We'll build the creative engine that gets you there."
      />
    </div>
  );
}
